import type { DeliveryGroupDraft, DeliveryGroupStopDraft } from './deliveryGroupTypes';
import {
  buildAllocationLinksFromUidDrafts,
  ensureSavePayloadLoadingUnloadingLinks,
  type LoadingUnloadingAllocationLink
} from './loadingUnloadingLinkHelpers';
import type { FromStopDraftByUid, QuantityDraftByCargo, ToStopDraftByUid } from './executionDraftTypes';
import { validateExecutionGraph } from './validateExecutionGraph';
import { resolveRouteStopsSavePayload } from './resolveRouteStopsSavePayload';
import {
  buildStopUidToPositionMap,
  validateRouteStopsSavePayload,
  type MainStopFormFields,
  type RouteStopSaveRow
} from './routeStopsSavePipeline';

export type ExecutionLabelContext = {
  resolveWarehouseName?: (id: number) => string | undefined;
  resolveConsigneeName?: (id: number) => string | undefined;
};

export type ExecutionTopologyPreview = {
  saveRows: RouteStopSaveRow[];
  positionByStopUid: Map<string, number>;
  unloadingOptionsByGroupUid: Map<string, Array<{ value: number; label: string; stopUid: string }>>;
  loadingPositionByStopUid: Map<string, number>;
};

export type UnloadingStopOption = {
  value: number;
  label: string;
  stopUid: string;
};

export function buildExecutionTopologyPreview(params: {
  groups: DeliveryGroupDraft[];
  mainLoading: MainStopFormFields;
  mainUnloading: MainStopFormFields;
  labelContext?: ExecutionLabelContext;
}): ExecutionTopologyPreview {
  const { rows: saveRows } = resolveRouteStopsSavePayload({
    groups: params.groups,
    mainLoading: params.mainLoading,
    mainUnloading: params.mainUnloading
  });
  const positionByStopUid = buildStopUidToPositionMap(saveRows);
  const loadingPositionByStopUid = new Map<string, number>();
  const unloadingOptionsByGroupUid = new Map<string, UnloadingStopOption[]>();

  for (const row of saveRows) {
    if (!row._stopUid) continue;
    if (row.type === 'loading') {
      loadingPositionByStopUid.set(row._stopUid, row.position);
    }
  }

  for (const group of params.groups) {
    const options = buildGroupUnloadingOptionsFromSaveRows({
      group,
      saveRows,
      positionByStopUid,
      labelContext: params.labelContext
    });
    unloadingOptionsByGroupUid.set(group.uid, options);
  }

  return {
    saveRows,
    positionByStopUid,
    unloadingOptionsByGroupUid,
    loadingPositionByStopUid
  };
}

export function buildGroupUnloadingOptionsFromSaveRows(params: {
  group: DeliveryGroupDraft;
  saveRows: RouteStopSaveRow[];
  positionByStopUid: Map<string, number>;
  labelContext?: ExecutionLabelContext;
}): UnloadingStopOption[] {
  const { group, saveRows, positionByStopUid } = params;
  const options: UnloadingStopOption[] = [];
  const seenUids = new Set<string>();

  for (const row of saveRows) {
    if (row.type !== 'unloading') continue;
    if (row._groupUid !== group.uid) continue;
    if (!row._stopUid || seenUids.has(row._stopUid)) continue;
    seenUids.add(row._stopUid);

    const stop =
      findStopInGroup(group, row._stopUid) ?? stopDraftFromSaveRow(row, group.uid);
    const position = positionByStopUid.get(row._stopUid) ?? row.position;

    options.push({
      value: position,
      label: formatExecutionUnloadingLabel(stop, params.labelContext),
      stopUid: row._stopUid
    });
  }

  return options.sort((a, b) => a.value - b.value);
}

export function formatExecutionUnloadingLabel(
  stop: Pick<
    DeliveryGroupStopDraft,
    'pointType' | 'pointId' | 'address' | 'city' | 'contactPerson'
  >,
  ctx?: ExecutionLabelContext
): string {
  const pt = String(stop.pointType || '').toLowerCase();
  const city = stop.city?.trim() || '';
  const address = stop.address?.trim() || '';
  const locationLine = [city, address].filter(Boolean).join(', ');

  if (pt === 'consignee') {
    const name =
      stop.pointId != null
        ? ctx?.resolveConsigneeName?.(Number(stop.pointId))?.trim()
        : undefined;
    if (locationLine && name) return `${locationLine} — Получатель: ${name}`;
    if (locationLine) return locationLine;
    if (name) return `Получатель: ${name}`;
    if (stop.contactPerson?.trim()) return stop.contactPerson.trim();
    return 'Адрес получателя';
  }

  if (pt === 'warehouse') {
    const warehouseName =
      stop.pointId != null
        ? ctx?.resolveWarehouseName?.(Number(stop.pointId))?.trim()
        : undefined;
    if (warehouseName && locationLine) return `${warehouseName} — ${locationLine}`;
    if (warehouseName) return warehouseName;
    if (locationLine) return locationLine;
    return 'Склад';
  }

  if (locationLine) return locationLine;
  if (city) return city;
  if (address) return address;
  return 'Точка выгрузки';
}

export function applyExecutionTopologyToGroups(
  groups: DeliveryGroupDraft[],
  preview: ExecutionTopologyPreview
): DeliveryGroupDraft[] {
  return groups.map((group) => {
    const loadingStops = group.loadingStops.map((s) => {
      const nextPos = preview.positionByStopUid.get(s.uid);
      if (nextPos === undefined || s.position === nextPos) return s;
      return { ...s, position: nextPos };
    });
    const unloadingStops = group.unloadingStops.map((s) => {
      const nextPos = preview.positionByStopUid.get(s.uid);
      if (nextPos === undefined || s.position === nextPos) return s;
      return { ...s, position: nextPos };
    });
    const primaryLoadingPos =
      loadingStops[0]?.position ?? (group.isDefault ? 0 : group.loadingPosition);
    const loadingPosition =
      group.loadingPosition === primaryLoadingPos ? group.loadingPosition : primaryLoadingPos;
    if (
      loadingPosition === group.loadingPosition &&
      loadingStops.every((s, i) => s === group.loadingStops[i]) &&
      unloadingStops.every((s, i) => s === group.unloadingStops[i])
    ) {
      return group;
    }
    return { ...group, loadingStops, unloadingStops, loadingPosition };
  });
}

export function resolveLoadingPositionFromTopology(
  stopUid: string,
  preview: ExecutionTopologyPreview | null | undefined,
  fallback: number
): number {
  if (!preview) return fallback;
  return preview.loadingPositionByStopUid.get(stopUid) ?? fallback;
}

export function listLoadingPointsFromTopology(
  group: DeliveryGroupDraft,
  preview: ExecutionTopologyPreview | null | undefined,
  labelFallback = true
): Array<{ key: string; position: number; label: string; stopUid: string }> {
  if (group.loadingStops.length === 0) return [];

  return group.loadingStops.map((s, idx) => {
    const position =
      preview?.loadingPositionByStopUid.get(s.uid) ??
      preview?.positionByStopUid.get(s.uid) ??
      s.position ??
      (group.isDefault && idx === 0 ? 0 : group.isDefault ? idx : idx + 1);
    const label = s.address?.trim() || (labelFallback ? `Точка погрузки ${idx + 1}` : '');
    return { key: s.uid, position, label: label || `Точка погрузки ${idx + 1}`, stopUid: s.uid };
  });
}

export type RouteCreateExecutionValidationResult = {
  ok: boolean;
  message?: string;
};

export function validateExecutionTopologyBeforeRouteCreate(params: {
  groups: DeliveryGroupDraft[];
  mainLoading: MainStopFormFields;
  mainUnloading: MainStopFormFields;
  selectedIds: number[];
  fromStopDraft: FromStopDraftByUid;
  toStopDraft: ToStopDraftByUid;
  quantityDraft: QuantityDraftByCargo;
  allocationEdges?: import('./executionDraftTypes').AllocationEdgeDraft[];
  cargoApplicationById?: Record<number, number | undefined>;
  cargoAvailableById?: Record<number, number | undefined>;
  cargoCapById?: Record<number, number | undefined>;
}): RouteCreateExecutionValidationResult {
  const preview = buildExecutionTopologyPreview({
    groups: params.groups,
    mainLoading: params.mainLoading,
    mainUnloading: params.mainUnloading
  });

  const allocationLinks = buildAllocationLinksFromUidDrafts({
    selectedIds: params.selectedIds,
    fromStopDraft: params.fromStopDraft,
    toStopDraft: params.toStopDraft,
    quantityDraft: params.quantityDraft,
    positionByStopUid: preview.positionByStopUid,
    allocationEdges: params.allocationEdges
  });

  const saveRowsWithLinks = ensureSavePayloadLoadingUnloadingLinks(
    preview.saveRows,
    allocationLinks
  );

  const stopIssues = validateRouteStopsSavePayload(saveRowsWithLinks, { allocationLinks });
  if (stopIssues.length > 0) {
    return {
      ok: false,
      message: `Маршрут не может быть создан: ${stopIssues[0]?.message ?? 'некорректная топология точек'}`
    };
  }

  const unloadingStopUids = new Set(
    saveRowsWithLinks.filter((r) => r.type === 'unloading').map((r) => r._stopUid).filter(Boolean)
  );
  if (unloadingStopUids.size === 0) {
    return {
      ok: false,
      message: 'Маршрут не может быть создан: не заданы точки выгрузки'
    };
  }

  const execIssues = validateExecutionGraph({
    groups: params.groups,
    selectedIds: params.selectedIds,
    fromStopDraft: params.fromStopDraft,
    toStopDraft: params.toStopDraft,
    quantityDraft: params.quantityDraft,
    allocationEdges: params.allocationEdges,
    topology: preview,
    cargoApplicationById: params.cargoApplicationById,
    cargoAvailableById: params.cargoAvailableById,
    cargoCapById: params.cargoCapById
  });
  if (execIssues.length > 0) {
    return {
      ok: false,
      message: `Маршрут не может быть создан: ${execIssues[0]?.message}`
    };
  }

  for (const cargoId of params.selectedIds) {
    const qty = Number(params.quantityDraft[cargoId] ?? 0);
    if (qty <= 0) continue;
    const toUid = params.toStopDraft[cargoId];
    if (toUid && !preview.positionByStopUid.has(toUid)) {
      return {
        ok: false,
        message: 'Маршрут не может быть создан: обнаружено частичное распределение грузов'
      };
    }
  }

  return { ok: true };
}

/** Legacy position-based validator adapter */
export function validateExecutionTopologyBeforeRouteCreateLegacy(params: {
  groups: DeliveryGroupDraft[];
  mainLoading: MainStopFormFields;
  mainUnloading: MainStopFormFields;
  selectedIds: number[];
  fromStopDraft: Record<number, number | undefined>;
  toStopDraft: Record<number, number | undefined>;
  quantityDraft: Record<number, number>;
  cargoApplicationById?: Record<number, number | undefined>;
}): RouteCreateExecutionValidationResult {
  const preview = buildExecutionTopologyPreview({
    groups: params.groups,
    mainLoading: params.mainLoading,
    mainUnloading: params.mainUnloading
  });
  const fromUid: FromStopDraftByUid = {};
  const toUid: ToStopDraftByUid = {};
  const positionToUid = new Map<number, string>();
  for (const [uid, pos] of Array.from(preview.positionByStopUid.entries())) positionToUid.set(pos, uid);
  for (const id of params.selectedIds) {
    const fp = params.fromStopDraft[id];
    const tp = params.toStopDraft[id];
    if (fp !== undefined) fromUid[id] = positionToUid.get(fp);
    if (tp !== undefined) toUid[id] = positionToUid.get(tp);
  }
  return validateExecutionTopologyBeforeRouteCreate({
    ...params,
    fromStopDraft: fromUid,
    toStopDraft: toUid,
    quantityDraft: params.quantityDraft
  });
}

let lastTopologyDebugFingerprint: string | null = null;

export function logExecutionTopologyDebug(
  label: string,
  preview: ExecutionTopologyPreview,
  groups?: DeliveryGroupDraft[],
  extra?: { allocationLinks?: LoadingUnloadingAllocationLink[] }
): void {
  if (process.env.NODE_ENV === 'production') return;
  if (label === 'reactive') return;
  console.group(`[ExecutionTopology] ${label}`);
  console.log('unloadingAddressMap', Object.fromEntries(preview.unloadingOptionsByGroupUid));
  console.log('positionMapping', Object.fromEntries(preview.positionByStopUid));
  console.log('loadingScope', Object.fromEntries(preview.loadingPositionByStopUid));
  if (extra?.allocationLinks?.length) {
    console.log('allocationMap', extra.allocationLinks);
  }
  if (groups) {
    console.log(
      'loadingPoints',
      groups.map((g) => ({
        uid: g.uid,
        loading: g.loadingStops.map((x) => ({
          uid: x.uid,
          pos: x.position,
          address: x.address,
          contact: x.contactPerson,
          phone: x.phone
        })),
        unloading: g.unloadingStops.map((x) => ({
          uid: x.uid,
          pos: x.position,
          address: x.address
        }))
      }))
    );
  }
  console.groupEnd();
}

/** Log topology only when fingerprint changes (pre-save / allocation / finalize). */
export function logExecutionTopologyDebugOnChange(
  label: string,
  preview: ExecutionTopologyPreview,
  fingerprint: string | null,
  groups?: DeliveryGroupDraft[],
  extra?: { allocationLinks?: LoadingUnloadingAllocationLink[] }
): void {
  if (process.env.NODE_ENV === 'production') return;
  if (fingerprint != null && fingerprint === lastTopologyDebugFingerprint) return;
  lastTopologyDebugFingerprint = fingerprint;
  logExecutionTopologyDebug(label, preview, groups, extra);
}

export function resetExecutionTopologyDebugFingerprint(): void {
  lastTopologyDebugFingerprint = null;
}

export function logRouteCreateStageDebug(stage: string, detail?: unknown): void {
  if (process.env.NODE_ENV === 'production') return;
  console.log(`[RouteCreate] stage=${stage}`, detail ?? '');
}

export function logRouteCreateRollbackDebug(routeId: number, reason: unknown): void {
  if (process.env.NODE_ENV === 'production') return;
  console.warn(`[RouteCreate] rollback routeId=${routeId}`, reason);
}

function findStopInGroup(
  group: DeliveryGroupDraft,
  stopUid: string
): DeliveryGroupStopDraft | undefined {
  return (
    group.unloadingStops.find((s) => s.uid === stopUid) ??
    group.loadingStops.find((s) => s.uid === stopUid)
  );
}

function stopDraftFromSaveRow(row: RouteStopSaveRow, groupUid: string): DeliveryGroupStopDraft {
  return {
    uid: row._stopUid ?? `save-row-${row.position}`,
    groupUid,
    type: row.type,
    position: row.position,
    pointType: (row.pointType ?? null) as DeliveryGroupStopDraft['pointType'],
    pointId: row.pointId ?? null,
    address: row.address ?? null,
    city: row.city ?? null,
    contactPerson: row.contactPerson ?? null,
    phone: row.phone ?? null,
    plannedAt: row.plannedAt ?? null,
    actualAt: row.actualAt ?? null,
    notes: null,
    status: row.status ?? null
  };
}
